import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'bedtime', 'sensitivity',
    'coffee', 'coffeeType', 'espresso',
    'tea', 'teaType',
    'energyDrink', 'energyDrinkType',
    'preworkout', 'preworkoutType',
    'soda', 'sodaType',
    'chocolate',
    'totalCaffeine', 'status', 'statusBadge', 'resultContainer',
    'safetyIndicator', 'cutoffTime',
    'halfLife', 'clearTime75', 'clearTime90',
    'breakdown', 'insight'
  ]

  // Half-life in hours by sensitivity
  halfLifeHours = {
    low: 3.5,
    normal: 5.5,
    high: 9
  }

  connect() {
    this.calculate()
  }

  calculate() {
    // Calculate caffeine from each source
    const sources = this.calculateSources()
    const totalCaffeine = Object.values(sources).reduce((sum, val) => sum + val, 0)

    // Get sensitivity and bedtime
    const sensitivity = this.sensitivityTarget.value
    const bedtime = this.bedtimeTarget.value

    // Update displays
    this.totalCaffeineTarget.textContent = Math.round(totalCaffeine)

    // Update status
    this.updateStatus(totalCaffeine)

    // Update safety indicator
    this.updateSafetyIndicator(totalCaffeine)

    // Update cutoff time
    this.updateCutoffTime(bedtime, sensitivity)

    // Update half-life info
    this.updateHalfLifeInfo(sensitivity)

    // Update breakdown
    this.updateBreakdown(sources)

    // Update insight
    this.updateInsight(totalCaffeine, sensitivity)
  }

  calculateSources() {
    const coffee = (parseInt(this.coffeeTarget.value, 10) || 0) *
      (parseInt(this.coffeeTypeTarget.value, 10) || 95)
    const espresso = (parseInt(this.espressoTarget.value, 10) || 0) * 63
    const tea = (parseInt(this.teaTarget.value, 10) || 0) *
      (parseInt(this.teaTypeTarget.value, 10) || 47)
    const energyDrink = (parseInt(this.energyDrinkTarget.value, 10) || 0) *
      (parseInt(this.energyDrinkTypeTarget.value, 10) || 80)
    const preworkout = (parseFloat(this.preworkoutTarget.value) || 0) *
      (parseInt(this.preworkoutTypeTarget.value, 10) || 150)
    const soda = (parseInt(this.sodaTarget.value, 10) || 0) *
      (parseInt(this.sodaTypeTarget.value, 10) || 34)
    const chocolate = (parseInt(this.chocolateTarget.value, 10) || 0) * 23

    return {
      coffee,
      espresso,
      tea,
      energyDrink,
      preworkout,
      soda,
      chocolate
    }
  }

  updateStatus(total) {
    let status; let
      colorClass
    if (total <= 200) {
      status = 'LOW'
      colorClass = 'border-green-500 text-green-700'
    } else if (total <= 400) {
      status = 'MODERATE'
      colorClass = 'border-green-500 text-green-700'
    } else if (total <= 600) {
      status = 'HIGH'
      colorClass = 'border-yellow-500 text-yellow-700'
    } else {
      status = 'EXCESSIVE'
      colorClass = 'border-red-500 text-red-700'
    }

    this.statusTarget.textContent = status
    this.statusBadgeTarget.className = `inline-block px-4 py-2 border-2 font-medium text-sm ${colorClass}`

    // Update result container border
    const borderColor = colorClass.split(' ')[0]
    this.resultContainerTarget.className = `p-6 bg-white border-2 ${borderColor}`
  }

  updateSafetyIndicator(total) {
    // Map 0-600+ to 0-100%
    const percent = Math.min(100, (total / 600) * 100)
    this.safetyIndicatorTarget.style.left = `${percent}%`
  }

  updateCutoffTime(bedtime, sensitivity) {
    // Parse bedtime
    const [hours, minutes] = bedtime.split(':').map((n) => parseInt(n, 10))
    const bedtimeMinutes = hours * 60 + minutes

    // Calculate hours before bed to stop caffeine
    // We want <25mg caffeine at bedtime, which is ~2 half-lives from ~100mg dose
    const hoursBeforeBed = {
      low: 6,
      normal: 8,
      high: 10
    }

    const cutoffMinutes = bedtimeMinutes - (hoursBeforeBed[sensitivity] * 60)
    let cutoffHour = Math.floor(cutoffMinutes / 60)
    const cutoffMin = cutoffMinutes % 60

    // Handle negative (next day)
    if (cutoffHour < 0) cutoffHour += 24

    // Format time
    const period = cutoffHour >= 12 ? 'PM' : 'AM'
    let displayHour = cutoffHour
    if (cutoffHour > 12) {
      displayHour = cutoffHour - 12
    } else if (cutoffHour === 0) {
      displayHour = 12
    }
    const displayMin = cutoffMin.toString().padStart(2, '0')

    this.cutoffTimeTarget.textContent = `${displayHour}:${displayMin} ${period}`
  }

  updateHalfLifeInfo(sensitivity) {
    const _halfLife = this.halfLifeHours[sensitivity]

    const halfLifeRanges = {
      low: '3-4 hours',
      normal: '5-6 hours',
      high: '8-10 hours'
    }

    const clear75Ranges = {
      low: '6-8 hours',
      normal: '10-12 hours',
      high: '16-20 hours'
    }

    const clear90Ranges = {
      low: '10-14 hours',
      normal: '15-20 hours',
      high: '25-30 hours'
    }

    this.halfLifeTarget.textContent = halfLifeRanges[sensitivity]
    this.clearTime75Target.textContent = clear75Ranges[sensitivity]
    this.clearTime90Target.textContent = clear90Ranges[sensitivity]
  }

  updateBreakdown(sources) {
    const labels = {
      coffee: '☕ Coffee',
      espresso: '⚡ Espresso',
      tea: '🍵 Tea',
      energyDrink: '🥤 Energy Drinks',
      preworkout: '💪 Pre-workout',
      soda: '🥤 Soda',
      chocolate: '🍫 Chocolate'
    }

    const items = Object.entries(sources)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => `<div class="flex justify-between items-center pb-2 border-b border-gray-200">
          <span class="text-sm">${labels[key]}</span>
          <span class="font-mono font-bold">${Math.round(value)}mg</span>
        </div>`)

    if (items.length === 0) {
      this.breakdownTarget.innerHTML = '<p class="text-sm text-gray-500">No caffeine sources added</p>'
    } else {
      this.breakdownTarget.innerHTML = items.join('')
    }
  }

  updateInsight(total, sensitivity) {
    let insight

    if (total === 0) {
      insight = 'No caffeine intake recorded. If you consume caffeine, add your sources above to calculate your daily intake and optimal cutoff time.'
    } else if (total <= 200) {
      insight = 'Your caffeine intake is low and well within safe limits. This amount should provide mild alertness without significant sleep impact.'
    } else if (total <= 400) {
      if (sensitivity === 'high') {
        insight = 'While your intake is within general guidelines, your high sensitivity means caffeine stays in your system longer. ' +
          'Consider reducing intake or having your last caffeine earlier in the day.'
      } else {
        insight = 'Your caffeine intake is moderate and within the recommended daily limit of 400mg. ' +
          'For optimal sleep, ensure you finish caffeine consumption by the cutoff time shown.'
      }
    } else if (total <= 600) {
      insight = 'Your caffeine intake exceeds the recommended 400mg daily limit. Consider gradually reducing consumption ' +
        'to avoid tolerance buildup and potential side effects like anxiety, jitters, and sleep disruption.'
    } else {
      insight = 'Your caffeine intake is significantly above recommended levels. High doses can cause anxiety, ' +
        'heart palpitations, digestive issues, and severe sleep disruption. Consider reducing intake gradually to avoid withdrawal symptoms.'
    }

    this.insightTarget.textContent = insight
  }
}
